/**
 * Costo del personale (24/10/2026, 2.37, docs/adr/ADR-024): the company cost of
 * every employee, month by month, read from the payslips of the labour
 * consultant, and the cost of an hour actually worked.
 *
 * - gross = INPS base of the payslip less the company car fringe benefit
 *   (a taxable value, not money paid);
 * - company INPS = the total of the monthly summary, split among the payslips
 *   by their INPS base (the payslip does not show it);
 * - TFR = the quota of the payslip, to the fund or kept by the company;
 * - other = Metasalute, meal vouchers and welfare as on the payslip;
 * - while the tredicesima of the year is not paid, every month accrues 1/12 of
 *   it with its INPS and TFR; leave accruals are not added (decision 24/10);
 * - €/hour = cost ÷ hours worked (ordinary + overtime, as on the payslip).
 *
 * Reserved: the figures leave the API only with the feature «persone.costs».
 */
import { z } from 'zod';
import { addDays } from './dates.js';
import { fromCents, normaliseDecimal, toCents } from './money.js';
import { salaryPeriodLabel } from './people.js';
import { isoDateSchema, optionalText } from './validation.js';
/** The payroll software of the consultant, recognised from the PDF (2.37: the one used since 2026). */
export const PAYROLL_FORMATS = ['essepaghe'];
export const PAYROLL_FORMAT_LABELS = {
    essepaghe: 'Essepaghe (consulente dal 2026)',
};
/** The PDFs of a month, as the browser sends them: the content in base64. */
export const payrollFilesSchema = z.object({
    files: z
        .array(z.object({
        name: z.string().trim().min(1).max(200),
        content: z.string().min(1).max(8_000_000),
    }))
        .min(1, { message: 'Scegli i PDF del mese' })
        .max(5, { message: 'Al massimo 5 file' }),
});
/* --- Rules ------------------------------------------------------------------- */
/**
 * Splits whole cents by weights, the remainders to the largest fractions: the
 * parts always add up to the total.
 */
export function allocateCents(total, weights) {
    const sum = weights.reduce((acc, weight) => acc + weight, 0);
    if (sum === 0)
        return weights.map(() => 0);
    const exact = weights.map((weight) => (total * weight) / sum);
    const parts = exact.map((value) => Math.floor(value));
    let left = total - parts.reduce((acc, part) => acc + part, 0);
    const order = exact
        .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
        .sort((a, b) => b.fraction - a.fraction || a.index - b.index);
    for (const { index } of order) {
        if (left <= 0)
            break;
        parts[index] += 1;
        left -= 1;
    }
    return parts;
}
/** The company INPS of the summary split among the payslips by their INPS base. */
export function companyInps(slips, total) {
    return allocateCents(toCents(total), slips.map((slip) => toCents(slip.inpsBase))).map(fromCents);
}
/** The gross pay of a payslip: the INPS base less the car fringe benefit. */
export function slipGross(slip) {
    return toCents(slip.inpsBase) - toCents(slip.fringeCar);
}
const sumCents = (items, pick) => items.reduce((acc, item) => acc + toCents(pick(item)), 0);
/** The checks of a month before it is saved. */
export function payrollChecks(read, employees, previousInpsBase) {
    const checks = [];
    const known = new Set(employees.map((employee) => employee.taxCode));
    const period = salaryPeriodLabel(read.year, read.month).toLowerCase();
    if (read.slips.length === 0) {
        checks.push({ level: 'error', message: `Nessun cedolino di ${period} nei file scelti.` });
    }
    for (const slip of read.slips) {
        if (!known.has(slip.taxCode)) {
            checks.push({
                level: 'error',
                message: `${slip.name} (${slip.taxCode}) non è in Persone: aggiungilo prima di caricare il mese.`,
            });
        }
    }
    if (!read.totals) {
        checks.push({
            level: 'error',
            message: 'Manca il riepilogo del mese («Riepiloghi»): serve per l’INPS azienda e i totali.',
        });
        return checks;
    }
    const totals = read.totals;
    const gross = read.slips.reduce((acc, slip) => acc + slipGross(slip), 0);
    const grossDifference = gross - toCents(totals.grossCost);
    // The INPS base is rounded to the euro: up to 1 € per payslip.
    checks.push(Math.abs(grossDifference) <= 100 * Math.max(1, read.slips.length)
        ? {
            level: 'ok',
            message: `Lordo dei cedolini ${money(gross)} = «Costo retribuzioni» del riepilogo ${money(toCents(totals.grossCost))} (differenza ${money(Math.abs(grossDifference))}).`,
        }
        : {
            level: 'warn',
            message: `Lordo dei cedolini ${money(gross)} diverso dal «Costo retribuzioni» del riepilogo ${money(toCents(totals.grossCost))}: differenza ${money(grossDifference)}.`,
        });
    const net = sumCents(read.slips, (slip) => slip.net);
    checks.push(net === toCents(totals.net)
        ? { level: 'ok', message: `Netti dei cedolini ${money(net)} = netto a pagare del riepilogo.` }
        : {
            level: 'warn',
            message: `Netti dei cedolini ${money(net)} diversi dal netto a pagare del riepilogo ${money(toCents(totals.net))}.`,
        });
    const metasalute = sumCents(read.slips, (slip) => slip.metasalute);
    if (metasalute !== toCents(totals.metasalute)) {
        checks.push({
            level: 'warn',
            message: `Metasalute dei cedolini ${money(metasalute)} diverso dal riepilogo ${money(toCents(totals.metasalute))}.`,
        });
    }
    if (totals.employees !== read.slips.length) {
        checks.push({
            level: 'warn',
            message: `Il riepilogo conta ${totals.employees} dipendenti, i cedolini sono ${read.slips.length}.`,
        });
    }
    const base = sumCents(read.slips, (slip) => slip.inpsBase);
    if (base > 0) {
        checks.push({
            level: 'ok',
            message: `INPS azienda ${money(toCents(totals.inpsCompany))} ripartita sull’imponibile (aliquota media ${((toCents(totals.inpsCompany) / base) * 100).toFixed(2).replace('.', ',')} %).`,
        });
    }
    for (const slip of read.slips) {
        const before = previousInpsBase.get(slip.taxCode);
        if (!before || toCents(before) === 0)
            continue;
        const change = (toCents(slip.inpsBase) - toCents(before)) / toCents(before);
        if (Math.abs(change) > 0.15) {
            checks.push({
                level: 'warn',
                message: `${slip.name}: imponibile ${change > 0 ? '+' : ''}${Math.round(change * 100)} % rispetto al mese prima (aumento, arretrati, assenze?): da confermare.`,
            });
        }
    }
    return checks;
}
const money = (cents) => `${(cents / 100)
    .toFixed(2)
    .replace('.', ',')
    .replace(/\B(?=(\d{3})+(?!\d))/g, '.')} €`;
/**
 * The accrual of the tredicesima for a month: 1/12 of the monthly pay, with the
 * company INPS at the rate of the month and its TFR (÷ 13,5).
 */
export function thirteenthAccrual(slip) {
    const share = toCents(slip.tfrBase) / 12;
    const base = toCents(slip.inpsBase);
    const rate = base > 0 ? toCents(slip.inpsCompany) / base : 0;
    return Math.round(share * (1 + rate) + share / 13.5);
}
/** Cents of the cost of one payslip, with the accrual of the tredicesima when asked. */
export function slipCost(slip, accrue) {
    const gross = slipGross(slip);
    const inpsCompany = toCents(slip.inpsCompany);
    const tfr = toCents(slip.tfrQuota);
    const other = toCents(slip.metasalute) + toCents(slip.mealVouchers) + toCents(slip.welfare);
    const accrual = accrue && !slip.thirteenth ? thirteenthAccrual(slip) : 0;
    return {
        gross,
        inpsCompany,
        tfr,
        other,
        thirteenthAccrual: accrual,
        total: gross + inpsCompany + tfr + other + accrual,
        hours: toCents(slip.hoursWorked) + toCents(slip.overtimeHours),
    };
}
/** €/hour from cents of cost and hundredths of hours; null without hours. */
export function hourlyCost(costCents, hoursHundredths) {
    return hoursHundredths > 0 ? fromCents(Math.round((costCents * 100) / hoursHundredths)) : null;
}
/** The figures of a set of payslips, summed. */
export function costFigures(slips, accrue) {
    const sum = {
        gross: 0,
        inpsCompany: 0,
        tfr: 0,
        other: 0,
        thirteenthAccrual: 0,
        total: 0,
        hours: 0,
    };
    for (const slip of slips) {
        const cost = slipCost(slip, accrue);
        for (const key of Object.keys(sum))
            sum[key] += cost[key];
    }
    return {
        gross: fromCents(sum.gross),
        inpsCompany: fromCents(sum.inpsCompany),
        tfr: fromCents(sum.tfr),
        other: fromCents(sum.other),
        thirteenthAccrual: fromCents(sum.thirteenthAccrual),
        total: fromCents(sum.total),
        hours: fromCents(sum.hours),
        hourly: hourlyCost(sum.total, sum.hours),
    };
}
/** Whether the months of a year accrue the tredicesima: none of its payslips paid it yet. */
export function accruesThirteenth(slips) {
    return !slips.some((slip) => slip.month === 13 || slip.thirteenth);
}
/* --- Collaborators (2.38) ------------------------------------------------------ */
/**
 * The cost of an external collaborator is typed by hand (decision 24/10/2026):
 * by the hour, or by the day with the hours of the day, from a date on. It
 * stays until the next one. The hours, and so the cost of a period, come with
 * K-Presenze.
 */
export const RATE_UNITS = ['ora', 'giorno'];
export const RATE_UNIT_LABELS = { ora: 'all’ora', giorno: 'al giorno' };
const decimalText = (message, max) => z
    .string()
    .trim()
    .transform((value, context) => {
    const normalised = normaliseDecimal(value);
    const number = normalised === null ? NaN : Number(normalised);
    if (!(number > 0) || number > max) {
        context.addIssue({ code: 'custom', message });
        return z.NEVER;
    }
    return fromCents(Math.round(number * 100));
});
export const collaboratorRateSchema = z
    .object({
    validFrom: isoDateSchema,
    unit: z.enum(RATE_UNITS),
    amount: decimalText('Importo non valido', 1_000_000),
    hoursPerDay: decimalText('Ore al giorno tra 0,5 e 24', 24).nullable(),
    notes: optionalText(200),
})
    .superRefine((body, context) => {
    if (body.unit === 'giorno' && !body.hoursPerDay) {
        context.addIssue({
            code: 'custom',
            path: ['hoursPerDay'],
            message: 'Indica le ore del giorno',
        });
    }
})
    .transform((body) => (body.unit === 'ora' ? { ...body, hoursPerDay: null } : body));
/** €/hour of a typed cost: the hourly amount, or the daily one ÷ the hours of the day. */
export function rateHourly(rate) {
    if (rate.unit === 'ora')
        return fromCents(toCents(rate.amount));
    const hours = rate.hoursPerDay ? toCents(rate.hoursPerDay) : 0;
    return hours > 0 ? fromCents(Math.round((toCents(rate.amount) * 100) / hours)) : '0.00';
}
/** The costs oldest first, each valid until the day before the next. */
export function ratePeriods(rates) {
    const sorted = [...rates].sort((a, b) => a.validFrom.localeCompare(b.validFrom));
    return sorted.map((rate, index) => {
        const next = sorted[index + 1];
        return {
            ...rate,
            validTo: next ? addDays(next.validFrom, -1) : null,
            hourly: rateHourly(rate),
        };
    });
}
/** The cost in force on a day, or null before the first one. */
export function rateInForce(periods, day) {
    return (periods.find((rate) => rate.validFrom <= day && (rate.validTo === null || rate.validTo >= day)) ?? null);
}
//# sourceMappingURL=payroll.js.map